import { z } from "zod";
import type { Category } from "./api.js";

/**
 * นำเข้า/ส่งออกราคากลางด้วย Excel (Admin ทำ manual) ตรวจสอบเป็น pure function ใช้ร่วม client (พรีวิว) และ server (ตัดสินจริง)
 * ไฟล์ = แถวหัวคอลัมน์ภาษาอังกฤษตาม PRICE_SHEET_COLUMNS 1 แถวต่อหมวด
 */
export const PRICE_SHEET_COLUMNS = [
  "category_id",
  "name_th",
  "name_en",
  "unit",
  "price_version",
  "labor_min_thb",
  "labor_max_thb",
  "material_min_thb",
  "material_max_thb",
  "note",
] as const;
export type PriceSheetColumn = (typeof PRICE_SHEET_COLUMNS)[number];

/** คอลัมน์ที่ต้องกรอกครบทุกแถวก่อนนำเข้า */
export const PRICE_REQUIRED_COLUMNS: readonly PriceSheetColumn[] = ["category_id", "price_version", "labor_min_thb", "labor_max_thb", "material_min_thb", "material_max_thb"];

export const MAX_IMPORT_ROWS = 100;
export const BIG_CHANGE_RATIO = 0.5;

/** แถวดิบจากไฟล์ (ค่าอาจเป็นข้อความ/ว่าง/ผิดชนิด) */
export type RawPriceRow = Partial<Record<PriceSheetColumn, unknown>>;

export const importSourceSchema = z.object({
  name: z.string().trim().min(1).max(200),
  kind_label_th: z.string().trim().min(1).max(80).default("ตารางราคาที่ Admin นำเข้าจาก Excel"),
  url: z.string().trim().url().max(500).startsWith("https://").or(z.literal("")).default(""),
  effective_from: z.string().date(),
});
export type ImportSource = z.infer<typeof importSourceSchema>;

export const importRequestSchema = z.object({
  filename: z.string().trim().min(1).max(200),
  source: importSourceSchema,
  rows: z.array(z.record(z.string(), z.unknown())).min(1).max(MAX_IMPORT_ROWS),
  /** ยืนยันรับทราบรายการที่ราคาเปลี่ยนมากกว่า 50% (บังคับเมื่อมี) */
  acknowledgeBigChanges: z.boolean().optional(),
});

export type ImportIssueCode =
  | "required"
  | "not_a_number"
  | "negative"
  | "too_large"
  | "min_gt_max"
  | "unknown_category"
  | "duplicate_category"
  | "stale_version"
  | "unit_mismatch"
  | "note_too_long"
  | "too_many_rows"
  | "bad_source_name"
  | "bad_source_date"
  | "bad_source_url";

export interface ImportIssue {
  /** แถวที่ 1 = แถวข้อมูลแรกในไฟล์ (ไม่นับหัวตาราง); 0 = ปัญหาระดับไฟล์/แหล่งอ้างอิง */
  row: number;
  field?: PriceSheetColumn | "source";
  code: ImportIssueCode;
  category_id?: string;
}

type Prices = Pick<Category, "labor_min_thb" | "labor_max_thb" | "material_min_thb" | "material_max_thb">;
export interface ImportChange {
  category_id: string;
  name_th: string;
  before: Prices;
  after: Prices;
  /** การเปลี่ยนแปลงสูงสุดของ 4 ช่อง เป็นสัดส่วนเทียบค่าเดิม (0.5 = 50%) */
  maxChangeRatio: number;
  bigChange: boolean;
  note?: string;
}

export interface ImportValidation {
  ok: boolean;
  errors: ImportIssue[];
  changes: ImportChange[];
  unchanged: string[];
  /** หมวดที่ไม่อยู่ในไฟล์ (คงราคาเดิม) */
  missingCategories: string[];
  hasBigChange: boolean;
}

const MAX_PRICE = 10_000_000;
const NUMERIC: PriceSheetColumn[] = ["labor_min_thb", "labor_max_thb", "material_min_thb", "material_max_thb"];

const isBlank = (v: unknown) => v === undefined || v === null || (typeof v === "string" && v.trim() === "");

/** แปลงค่าจาก Excel เป็นตัวเลข รองรับเลขที่เป็นข้อความ/มีคอมมา; อย่างอื่นคืน NaN */
export function toNumber(v: unknown): number {
  if (typeof v === "number") return v;
  if (typeof v === "string" && /^-?\d{1,3}(,\d{3})*(\.\d+)?$|^-?\d+(\.\d+)?$/.test(v.trim())) return Number(v.trim().replace(/,/g, ""));
  return Number.NaN;
}

const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

export function validatePriceImport(rows: readonly RawPriceRow[], categories: readonly Category[], source: unknown): ImportValidation {
  const errors: ImportIssue[] = [];
  const src = importSourceSchema.safeParse(source);
  if (!src.success) {
    for (const i of src.error.issues) {
      const k = String(i.path[0]);
      errors.push({ row: 0, field: "source", code: k === "name" ? "bad_source_name" : k === "effective_from" ? "bad_source_date" : "bad_source_url" });
    }
  }
  if (rows.length === 0) errors.push({ row: 0, code: "required" });
  if (rows.length > MAX_IMPORT_ROWS) errors.push({ row: 0, code: "too_many_rows" });

  const byId = new Map(categories.map((c) => [c.category_id, c]));
  const seen = new Set<string>();
  const changes: ImportChange[] = [];
  const unchanged: string[] = [];

  rows.slice(0, MAX_IMPORT_ROWS).forEach((r, idx) => {
    const row = idx + 1;
    const id = typeof r.category_id === "string" ? r.category_id.trim() : "";
    const issue = (code: ImportIssueCode, field?: PriceSheetColumn) => errors.push({ row, ...(field ? { field } : {}), code, ...(id ? { category_id: id } : {}) });

    for (const c of PRICE_REQUIRED_COLUMNS) if (isBlank(r[c])) issue("required", c);
    if (id) {
      if (seen.has(id)) issue("duplicate_category", "category_id");
      seen.add(id);
    }
    const cat = id ? byId.get(id) : undefined;
    if (id && !cat) issue("unknown_category", "category_id");

    const nums: Partial<Record<PriceSheetColumn, number>> = {};
    for (const c of NUMERIC) {
      if (isBlank(r[c])) continue;
      const n = toNumber(r[c]);
      if (Number.isNaN(n) || !Number.isFinite(n)) issue("not_a_number", c);
      else if (n < 0) issue("negative", c);
      else if (n > MAX_PRICE) issue("too_large", c);
      else nums[c] = round2(n);
    }
    if (nums.labor_min_thb !== undefined && nums.labor_max_thb !== undefined && nums.labor_min_thb > nums.labor_max_thb) issue("min_gt_max", "labor_min_thb");
    if (nums.material_min_thb !== undefined && nums.material_max_thb !== undefined && nums.material_min_thb > nums.material_max_thb) issue("min_gt_max", "material_min_thb");

    if (cat) {
      // ไฟล์ที่ดาวน์โหลดไว้ก่อนราคาถูกแก้จากที่อื่น ต้องดาวน์โหลดใหม่ ไม่ทับงานของคนอื่น
      if (!isBlank(r.price_version) && toNumber(r.price_version) !== cat.price_version) issue("stale_version", "price_version");
      if (!isBlank(r.unit) && String(r.unit).trim() !== cat.unit) issue("unit_mismatch", "unit");
    }
    if (!isBlank(r.note) && String(r.note).length > 300) issue("note_too_long", "note");

    if (cat && NUMERIC.every((c) => nums[c] !== undefined)) {
      const before: Prices = { labor_min_thb: cat.labor_min_thb, labor_max_thb: cat.labor_max_thb, material_min_thb: cat.material_min_thb, material_max_thb: cat.material_max_thb };
      const after: Prices = { labor_min_thb: nums.labor_min_thb!, labor_max_thb: nums.labor_max_thb!, material_min_thb: nums.material_min_thb!, material_max_thb: nums.material_max_thb! };
      const same = (Object.keys(before) as (keyof Prices)[]).every((k) => round2(before[k]) === after[k]);
      if (same) unchanged.push(id);
      else {
        const ratio = Math.max(...(Object.keys(before) as (keyof Prices)[]).map((k) => (before[k] > 0 ? Math.abs(after[k] - before[k]) / before[k] : after[k] > 0 ? 1 : 0)));
        changes.push({ category_id: id, name_th: cat.name_th, before, after, maxChangeRatio: Math.round(ratio * 1000) / 1000, bigChange: ratio > BIG_CHANGE_RATIO, ...(isBlank(r.note) ? {} : { note: String(r.note).trim() }) });
      }
    }
  });

  const missingCategories = categories.filter((c) => !seen.has(c.category_id)).map((c) => c.category_id);
  return { ok: errors.length === 0, errors, changes, unchanged, missingCategories, hasBigChange: changes.some((c) => c.bigChange) };
}

/** แถวสำหรับส่งออก (ราคาปัจจุบัน + เวอร์ชัน) */
export function exportPriceRows(categories: readonly Category[]): Record<PriceSheetColumn, string | number>[] {
  return [...categories]
    .sort((a, b) => a.display_order - b.display_order)
    .map((c) => ({
      category_id: c.category_id,
      name_th: c.name_th,
      name_en: c.name_en,
      unit: c.unit,
      price_version: c.price_version,
      labor_min_thb: c.labor_min_thb,
      labor_max_thb: c.labor_max_thb,
      material_min_thb: c.material_min_thb,
      material_max_thb: c.material_max_thb,
      note: "",
    }));
}
